import {currentUser} from './FirestoreService';

type CachedToken = {
  token: string;
  expiration: number;
  inProgress: false | Promise<CachedToken>;
};

export type SupportedToken = [
  'arcGisServerToken',
  'arcGisDatapointToken',
  'fiberLocatorToken',
  'algoliaKey',
][number];

// TODO: set up automatic token refresh based on expiration
// TODO: the returned value should be an object reference (or even a vue ref) so that the value can be updated in place
// after returning it to the caller
// TODO: cache tokens in local storage to minimize cold-start hits on page load

export class TokenService {
  private static cachedTokens: Map<SupportedToken, CachedToken> = new Map();

  /** Test-only: the cache is a private static, so specs reset it between tests. */
  static _resetCacheForTests(): void {
    this.cachedTokens.clear();
  }

  static async getToken(name: SupportedToken): Promise<string> {
    let cachedToken = this.cachedTokens.get(name);
    if (!cachedToken || cachedToken.expiration < Date.now()) {
      cachedToken = await this.fetchToken(name);
    }
    if (cachedToken && cachedToken.inProgress) {
      cachedToken = await cachedToken.inProgress;
    }
    return cachedToken.token;
  }

  private static async fetchToken(name: SupportedToken): Promise<CachedToken> {
    let token = this.cachedTokens.get(name);
    if (token?.inProgress) return await token.inProgress;

    let done!: (value: CachedToken) => void;
    let fail!: (reason: unknown) => void;
    const inProgress = new Promise<CachedToken>((resolve, reject) => {
      done = resolve;
      fail = reject;
    });
    // Suppresses an unobserved rejection when nothing else is awaiting; this call's
    // own caller still gets the error from the throw below.
    inProgress.catch(() => {});

    if (!token) {
      token = {
        token: '',
        expiration: 0,
        inProgress,
      };
      this.cachedTokens.set(name, token);
    }
    token.inProgress = inProgress;

    const cfUrl = process.env.VUE_APP_CLOUD_FUNCTIONS_URL;
    const useProdGCP = process.env.VUE_APP_GCP_ENV === 'production';
    const tokenUrl = `${cfUrl}/retrieveMapsToken${useProdGCP ? '' : '-staging'}?retrieve=${name}`;

    try {
      const user = currentUser();
      if (!user) throw new Error('Cannot fetch a maps token: user is not authenticated');
      const idToken = await user.getIdToken(true);
      const response = await fetch(tokenUrl, {method: 'GET', headers: {Authorization: `Bearer ${idToken}`}});
      // TODO: Handle HTTP and JSON errors
      const responseJson = await response.json();
      // TODO: standardize the response format in retrieveMapsToken
      token.token = responseJson.token || responseJson;
      token.expiration = responseJson.expires || (Date.now() + 1000 * 60 * 60 * 1); // Default to 1 hour
      token.inProgress = false;
      done(token);
      return token;
    } catch (err) {
      // Discard the entry: left cached with expiration 0 and an unsettled promise,
      // every later call re-enters here, finds `inProgress`, and awaits it forever.
      this.cachedTokens.delete(name);
      token.inProgress = false;
      fail(err);
      throw err;
    }
  }
}
